/**
 * Reglages du generateur de mots de passe (« Minimum numbers / Minimum special » de Bitwarden, « Exclude
 * characters » de KeePassXC, jeu de symboles impose par un site) : ce que l'ecran du generateur expose
 * au-dessus de `generatePassword` du coeur crypto, qui savait deja tout cela sans que l'IHM le propose.
 *
 * Ce sont des preferences, jamais des secrets : elles vivent en clair dans `localStorage` (comme chez
 * Bitwarden) et se relisent comme une donnee NON FIABLE -- chaque champ hors bornes retombe a son defaut.
 * Le verdict « ces reglages produisent un mot de passe » appartient au coeur (`passwordEntropyBits` leve sinon) :
 * `generatorProblem` ne fait que le traduire en une phrase qui dit quoi changer.
 */
import { AMBIGUOUS_CHARACTERS, CHARSET, MIN_PASSWORD_LENGTH, passwordEntropyBits, type PasswordGeneratorOptions } from '@klavox/crypto-core';
import {
  DEFAULT_PASSPHRASE_SETTINGS,
  MAX_PASSPHRASE_WORDS,
  MIN_PASSPHRASE_WORDS,
  PASSPHRASE_SEPARATORS,
  type PassphraseSettings,
} from '../core/generator.js';

/** Cle `localStorage` : une preference de cet appareil, jamais un secret. */
export const GENERATOR_OPTIONS_KEY = 'klavox:generator-options';

/** Bornes du curseur de longueur. */
export const GENERATOR_LENGTH_MIN = MIN_PASSWORD_LENGTH;
export const GENERATOR_LENGTH_MAX = 128;
/** Minimum de chiffres / de symboles : de 0 a 9, comme Bitwarden. */
export const GENERATOR_MINIMUM_MAX = 9;

export interface GeneratorPreferences {
  readonly length: number;
  readonly lowercase: boolean;
  readonly uppercase: boolean;
  readonly digits: boolean;
  readonly symbols: boolean;
  readonly minDigits: number;
  readonly minSymbols: number;
  readonly excludeAmbiguous: boolean;
  /** Caracteres a ne jamais tirer (contrainte d'un site) ; vide = aucun. */
  readonly excludeCharacters: string;
  /** Seuls symboles autorises (contrainte d'un site) ; vide = jeu par defaut du coeur. */
  readonly symbolCharacters: string;
}

export const DEFAULT_GENERATOR_PREFERENCES: GeneratorPreferences = {
  length: 20,
  lowercase: true,
  uppercase: true,
  digits: true,
  symbols: true,
  minDigits: 1,
  minSymbols: 1,
  excludeAmbiguous: false,
  excludeCharacters: '',
  symbolCharacters: '',
};

/** ASCII imprimable hors espace : un caractere qu'un champ de mot de passe accepte partout. */
function isPrintableAscii(char: string): boolean {
  return /^[\x21-\x7e]$/u.test(char);
}

/** Caracteres a exclure : ASCII imprimable, sans doublon, dans l'ordre saisi. */
export function cleanExcludedCharacters(raw: string): string {
  return [...new Set([...raw])].filter(isPrintableAscii).join('');
}

/**
 * Symboles autorises : ponctuation ASCII seulement, sans doublon. Une lettre ou un chiffre glisse ici
 * gonflerait le jeu « symboles » d'un caractere qui n'en est pas un, et le minimum de symboles mentirait.
 */
export function cleanSymbolCharacters(raw: string): string {
  return [...new Set([...raw])].filter((char) => isPrintableAscii(char) && !/^[A-Za-z0-9]$/u.test(char)).join('');
}

function boundedInteger(raw: unknown, min: number, max: number, fallback: number): number {
  const value = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : Number.NaN;
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.round(value)));
}

/**
 * Lit le formulaire du generateur. Un champ absent (desactive, donc hors `FormData`) garde la valeur
 * precedente : decocher « Chiffres » ne doit pas faire oublier le minimum choisi.
 */
export function generatorPreferencesFromForm(
  data: { get(name: string): FormDataEntryValue | null; has(name: string): boolean },
  previous: GeneratorPreferences,
): GeneratorPreferences {
  const text = (name: string): string | null => (data.has(name) ? String(data.get(name)) : null);
  const length = text('length');
  const minDigits = text('minDigits');
  const minSymbols = text('minSymbols');
  const excludeCharacters = text('excludeCharacters');
  const symbolCharacters = text('symbolCharacters');
  return {
    length: length === null ? previous.length : boundedInteger(length, GENERATOR_LENGTH_MIN, GENERATOR_LENGTH_MAX, previous.length),
    lowercase: data.has('lowercase'),
    uppercase: data.has('uppercase'),
    digits: data.has('digits'),
    symbols: data.has('symbols'),
    minDigits: minDigits === null ? previous.minDigits : boundedInteger(minDigits, 0, GENERATOR_MINIMUM_MAX, previous.minDigits),
    minSymbols: minSymbols === null ? previous.minSymbols : boundedInteger(minSymbols, 0, GENERATOR_MINIMUM_MAX, previous.minSymbols),
    excludeAmbiguous: data.has('excludeAmbiguous'),
    excludeCharacters: excludeCharacters === null ? previous.excludeCharacters : cleanExcludedCharacters(excludeCharacters),
    symbolCharacters: symbolCharacters === null ? previous.symbolCharacters : cleanSymbolCharacters(symbolCharacters),
  };
}

/** Les options du coeur crypto. Minuscules et majuscules gardent leur minimum de 1 (defaut du coeur). */
export function toGeneratorOptions(preferences: GeneratorPreferences): PasswordGeneratorOptions {
  return {
    length: preferences.length,
    lowercase: preferences.lowercase,
    uppercase: preferences.uppercase,
    digits: preferences.digits,
    symbols: preferences.symbols,
    minDigits: preferences.minDigits,
    minSymbols: preferences.minSymbols,
    excludeAmbiguous: preferences.excludeAmbiguous,
    ...(preferences.excludeCharacters === '' ? {} : { excludeCharacters: preferences.excludeCharacters }),
    ...(preferences.symbolCharacters === '' ? {} : { symbolCharacters: preferences.symbolCharacters }),
  };
}

const CATEGORY_LABELS = [
  { key: 'lowercase', pool: CHARSET.lowercase, label: 'minuscules' },
  { key: 'uppercase', pool: CHARSET.uppercase, label: 'majuscules' },
  { key: 'digits', pool: CHARSET.digits, label: 'chiffres' },
  { key: 'symbols', pool: null, label: 'symboles' },
] as const;

/**
 * `null` si le coeur accepte ces reglages ; sinon, la phrase qui dit quoi changer. Le verdict vient du coeur
 * (`passwordEntropyBits` leve sur des reglages impossibles) ; seule la phrase est construite ici.
 */
export function generatorProblem(preferences: GeneratorPreferences): string | null {
  try {
    passwordEntropyBits(toGeneratorOptions(preferences));
    return null;
  } catch {
    // Reglages refuses : on cherche lequel, pour le dire.
  }
  const enabled = CATEGORY_LABELS.filter((category) => preferences[category.key]);
  if (enabled.length === 0) return 'Cochez au moins un jeu de caracteres.';
  const excluded = new Set([...preferences.excludeCharacters]);
  if (preferences.excludeAmbiguous) {
    for (const char of AMBIGUOUS_CHARACTERS) excluded.add(char);
  }
  for (const category of enabled) {
    const pool = category.pool ?? (preferences.symbolCharacters === '' ? CHARSET.symbols : preferences.symbolCharacters);
    if ([...pool].every((char) => excluded.has(char))) {
      return `Les exclusions retirent tous les ${category.label} : retirez-en une ou decochez « ${category.label} ».`;
    }
  }
  const minimum =
    (preferences.lowercase ? 1 : 0) +
    (preferences.uppercase ? 1 : 0) +
    (preferences.digits ? preferences.minDigits : 0) +
    (preferences.symbols ? preferences.minSymbols : 0);
  if (minimum > preferences.length) {
    return `Les minimums imposent ${minimum} caracteres pour une longueur de ${preferences.length} : allongez le mot de passe ou baissez un minimum.`;
  }
  return 'Ces reglages ne permettent pas de generer un mot de passe.';
}

/** Relecture stricte du `localStorage` : un champ absent, d'un mauvais type ou hors bornes retombe a son defaut. */
export function readGeneratorPreferences(raw: string | null): GeneratorPreferences {
  const defaults = DEFAULT_GENERATOR_PREFERENCES;
  if (raw === null) return defaults;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return defaults;
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return defaults;
  const record = parsed as Record<string, unknown>;
  const flag = (name: keyof GeneratorPreferences): boolean =>
    typeof record[name] === 'boolean' ? (record[name] as boolean) : (defaults[name] as boolean);
  const integer = (name: 'length' | 'minDigits' | 'minSymbols', min: number, max: number): number =>
    typeof record[name] === 'number' && Number.isInteger(record[name]) && (record[name] as number) >= min && (record[name] as number) <= max
      ? (record[name] as number)
      : defaults[name];
  const preferences: GeneratorPreferences = {
    length: integer('length', GENERATOR_LENGTH_MIN, GENERATOR_LENGTH_MAX),
    lowercase: flag('lowercase'),
    uppercase: flag('uppercase'),
    digits: flag('digits'),
    symbols: flag('symbols'),
    minDigits: integer('minDigits', 0, GENERATOR_MINIMUM_MAX),
    minSymbols: integer('minSymbols', 0, GENERATOR_MINIMUM_MAX),
    excludeAmbiguous: flag('excludeAmbiguous'),
    excludeCharacters: typeof record.excludeCharacters === 'string' ? cleanExcludedCharacters(record.excludeCharacters) : '',
    symbolCharacters: typeof record.symbolCharacters === 'string' ? cleanSymbolCharacters(record.symbolCharacters) : '',
  };
  // Des reglages relus mais impossibles (trafiques) ne doivent pas bloquer « Generer » ailleurs dans l'app.
  return generatorProblem(preferences) === null ? preferences : defaults;
}

export function serializeGeneratorPreferences(preferences: GeneratorPreferences): string {
  return JSON.stringify(preferences);
}

/*
 * Phrase de passe et dernier type de generateur : gardes eux aussi sur l'appareil, comme chez Bitwarden.
 * Nombre de mots, langue, separateur, majuscule, chiffre : des preferences, jamais un secret ni une donnee
 * personnelle -- a la difference de l'adresse et du domaine du generateur d'identifiants, qui suivent le coffre.
 */

/** Cle `localStorage` des reglages de phrase de passe. */
export const PASSPHRASE_SETTINGS_KEY = 'klavox:passphrase-settings';
/** Cle `localStorage` du dernier type choisi (mot de passe, phrase de passe, identifiant, alias). */
export const GENERATOR_MODE_KEY = 'klavox:generator-mode';

export type GeneratorMode = 'password' | 'passphrase' | 'username' | 'alias';

const GENERATOR_MODES: readonly GeneratorMode[] = ['password', 'passphrase', 'username', 'alias'];

/** Type relu ou saisi : une valeur inconnue retombe sur « mot de passe ». */
export function readGeneratorMode(raw: string | null | undefined): GeneratorMode {
  return GENERATOR_MODES.find((mode) => mode === raw) ?? 'password';
}

function wordCount(raw: unknown, fallback: number): number {
  return typeof raw === 'number' && Number.isInteger(raw) && raw >= MIN_PASSPHRASE_WORDS && raw <= MAX_PASSPHRASE_WORDS ? raw : fallback;
}

function knownSeparator(raw: unknown, fallback: string): string {
  return PASSPHRASE_SEPARATORS.some((option) => option.value === raw) ? (raw as string) : fallback;
}

/** Lit le formulaire de phrase de passe : nombre de mots borne, separateur et langue pris dans leur liste. */
export function passphraseSettingsFromForm(data: { get(name: string): FormDataEntryValue | null }): PassphraseSettings {
  const defaults = DEFAULT_PASSPHRASE_SETTINGS;
  const words = Math.trunc(Number(data.get('words')));
  return {
    language: data.get('language') === 'en' ? 'en' : 'fr',
    words: Number.isFinite(words) && words !== 0 ? Math.min(MAX_PASSPHRASE_WORDS, Math.max(MIN_PASSPHRASE_WORDS, words)) : defaults.words,
    separator: knownSeparator(String(data.get('separator') ?? ''), defaults.separator),
    capitalize: data.get('capitalize') !== null,
    includeNumber: data.get('includeNumber') !== null,
  };
}

/**
 * Relecture stricte : un champ absent, d'un mauvais type ou hors bornes retombe a son defaut. Une phrase de
 * moins de 3 mots relue d'un stockage trafique ne doit jamais sortir du generateur.
 */
export function readPassphraseSettings(raw: string | null): PassphraseSettings {
  const defaults = DEFAULT_PASSPHRASE_SETTINGS;
  if (raw === null) return defaults;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return defaults;
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return defaults;
  const record = parsed as Record<string, unknown>;
  const flag = (name: 'capitalize' | 'includeNumber'): boolean => (typeof record[name] === 'boolean' ? (record[name] as boolean) : defaults[name]);
  return {
    language: record.language === 'en' || record.language === 'fr' ? record.language : defaults.language,
    words: wordCount(record.words, defaults.words),
    separator: knownSeparator(record.separator, defaults.separator),
    capitalize: flag('capitalize'),
    includeNumber: flag('includeNumber'),
  };
}

export function serializePassphraseSettings(settings: PassphraseSettings): string {
  return JSON.stringify(settings);
}
